import { HttpErrorResponse } from '@angular/common/http';
import { ErrorContext, ErrorProcessorConfig, ProcessedError } from './error-handler.types';
import { nanoid } from 'nanoid';

/**
 * Clase base para procesar errores.
 * Contiene toda la lógica compartida entre ErrorHandlerService y GlobalErrorHandlerService.
 *
 * IMPORTANTE: Todo el código aquí debe ser extremadamente defensivo y nunca debe fallar.
 */
export class ErrorProcessor {
  sessionId: string;
  config: ErrorProcessorConfig;

  constructor(config: Partial<ErrorProcessorConfig>) {
    this.sessionId = nanoid();

    this.config = <ErrorProcessorConfig>{
      ...config,
      production: config.production || false,
      filters: config.filters || {
        ignoreMessages: [],
        ignorePatterns: [],
        customFilter: null,
      },
    };
  }

  /**
   * Determina si un error debe ser ignorado según los filtros configurados.
   */
  shouldIgnoreError(errorMessage: string): boolean {
    try {
      // 1. Verificar mensajes exactos
      if (this.config.filters.ignoreMessages?.includes(errorMessage)) {
        return true;
      }

      // 2. Verificar patrones regex
      for (const pattern of this.config.filters.ignorePatterns || []) {
        try {
          if (pattern.test(errorMessage)) {
            return true;
          }
        } catch {
          // Si el regex falla, continuar con el siguiente
          continue;
        }
      }

      // 3. Verificar filtro personalizado
      if (this.config.filters.customFilter) {
        try {
          if (this.config.filters.customFilter(errorMessage)) {
            return true;
          }
        } catch {
          // Si el custom filter falla, no ignorar el error (más seguro)
        }
      }

      return false;
    } catch {
      // Si algo falla en el filtrado, no ignorar el error (más seguro)
      return false;
    }
  }

  /**
   * Extrae información detallada del error.
   */
  extractErrorInfo(error: any): ProcessedError {
    try {
      const processed: ProcessedError = {
        message: this.extractErrorMessage(error) || 'Unknown error',
      };

      // Detectar si está envuelto por zone.js
      let actualError = error;
      if (error?.ngOriginalError) {
        processed.wasZoneWrapped = true;
        actualError = error.ngOriginalError;
      }

      // Extraer información básica
      if (actualError instanceof Error) {
        processed.name = actualError.name;
        processed.stack = actualError.stack;
        processed.message = actualError.message || processed.message;
      }

      // Manejo específico para HttpErrorResponse
      if (actualError instanceof HttpErrorResponse) {
        this.extractHttpErrorInfo(actualError, processed);
      }

      // Serializar el error original de forma segura
      processed.originalError = this.serializeError(actualError);

      return processed;
    } catch {
      // Si falla la extracción, retornar error mínimo
      return {
        message: 'Failed to extract error information',
        originalError: this.serializeError(error),
      };
    }
  }

  /**
   * Extrae información específica de errores HTTP.
   */
  private extractHttpErrorInfo(error: HttpErrorResponse, processed: ProcessedError): void {
    try {
      processed.additionalInfo = processed.additionalInfo || {};
      processed.additionalInfo.httpStatus = error.status;
      processed.additionalInfo.httpUrl = error.url || undefined;
      processed.additionalInfo.httpMethod = error.statusText;

      // Extraer información del error del servidor
      if (error.error instanceof Error) {
        processed.additionalInfo.serverError = error.error.message;
      } else if (error.error instanceof ErrorEvent) {
        processed.additionalInfo.serverError = error.error.message;
      } else if (typeof error.error === 'string') {
        processed.additionalInfo.serverError = error.error;
      }
    } catch {
      // Si falla, no hacer nada
    }
  }

  /**
   * Captura el contexto actual de la aplicación.
   */
  captureContext(): ErrorContext {
    return {
      timestamp: new Date().toISOString(),
      url: this.getCurrentUrl(),
      userAgent: this.getUserAgent(),
      viewport: this.getViewport(),
      sessionId: this.sessionId,
      environment: {
        production: this.config.production,
        platform: typeof window !== 'undefined' ? 'browser' : 'server',
        system: this.config.system,
      },
    };
  }

  safeStringify(obj: any): string | null {
    try {
      const seen = new WeakSet();
      const serialized = JSON.stringify(obj, (key, value) => {
        // Manejar undefined explícitamente
        if (value === undefined) {
          return null; // Convertir undefined a null para evitar problemas en arrays
        }

        // Manejar funciones (no serializables)
        if (typeof value === 'function') {
          return '[Function]';
        }

        // Manejar referencias circulares
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular]';
          }
          seen.add(value);

          // Limitar profundidad para evitar stack overflow (opcional)
          // Puedes agregar un parámetro de profundidad si es necesario
        }

        // Manejar objetos Error que no fueron procesados
        if (value instanceof Error) {
          return {
            name: value.name,
            message: value.message,
            stack: value.stack,
          };
        }

        return value;
      });

      return serialized;
    } catch (error) {
      console.error('[CustomNGXLoggerServerService] safeStringify failed:', error);
      return null;
    }
  }

  /**
   * Extrae el mensaje del error de forma segura.
   */
  private extractErrorMessage(error: any): string {
    try {
      // Unwrap zone.js error
      if (error?.ngOriginalError) {
        error = error.ngOriginalError;
      }

      // String directo
      if (typeof error === 'string') {
        return error;
      }

      // Error object
      if (error instanceof Error) {
        return error.message;
      }

      // ErrorEvent
      if (error instanceof ErrorEvent) {
        return error.message || error.error?.message || '';
      }

      // HttpErrorResponse
      if (error instanceof HttpErrorResponse) {
        if (error.error instanceof Error) {
          return error.error.message;
        }
        if (error.error instanceof ErrorEvent) {
          return error.error.message;
        }
        if (typeof error.error === 'string') {
          return `HTTP ${error.status}: ${error.error}`;
        }
        return error.message;
      }

      // Objeto con propiedad message
      if (error?.message) {
        return String(error.message);
      }

      // Último recurso: convertir a string
      return String(error);
    } catch {
      return 'Unable to extract error message';
    }
  }

  /**
   * Serializa un error de forma segura, evitando referencias circulares.
   */
  private serializeError(error: any): string {
    try {
      if (error instanceof Error) {
        return this.safeStringify({
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }

      if (typeof error === 'string') {
        return error;
      }

      return this.safeStringify(error);
    } catch {
      // Si falla la serialización, retornar string simple
      return String(error);
    }
  }

  /**
   * Obtiene la URL actual de forma segura.
   */
  private getCurrentUrl(): string {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.href;
    }
    return 'unknown';
  }

  /**
   * Obtiene el user agent de forma segura.
   */
  private getUserAgent(): string {
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      return navigator.userAgent;
    }
    return 'unknown';
  }

  /**
   * Obtiene las dimensiones del viewport de forma segura.
   */
  private getViewport(): { width: number; height: number } | undefined {
    if (typeof window !== 'undefined') {
      return {
        width: window.innerWidth || 0,
        height: window.innerHeight || 0,
      };
    }
    return undefined;
  }
}
